import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { LeroyChat } from '@/components/LeroyChat';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { Home } from '@/pages/Home';
import { Shop } from '@/pages/Shop';
import { ProductDetail } from '@/pages/ProductDetail';
import { SkinAnalysisWizard } from '@/pages/SkinAnalysisWizard';
import { Profile } from '@/pages/Profile';
import { Regimen } from '@/pages/Regimen';
import { AgentPortal } from '@/pages/AgentPortal';
import { About } from '@/pages/About';
import { Blog } from '@/pages/Blog';
import { Article } from '@/pages/Article';
import { CheckoutPage } from '@/pages/CheckoutPage';
import { NotFound } from '@/pages/NotFound';
import { Contact, FAQ, ShippingReturns, Privacy, Terms } from '@/pages/StaticPages';
import { DesignSystemShowcase } from '@/components/DesignSystemShowcase';
import { BatchAnalysisPage } from '@/pages/BatchAnalysisPage';
import AdminPortal from '@/pages/AdminPortal';
import ShopBackofficePage from '@/pages/ShopBackofficePage';
import { MemberAuthProvider } from '@/context/MemberAuthContext';
import { ProfessionalAuthProvider } from '@/context/ProfessionalAuthContext';
import { SiteConfigProvider } from '@/context/SiteConfigContext';
import { DynamicFonts } from '@/components/DynamicFonts';
import { MemberRoutes } from '@/routes/memberRoutes';
import { ProfessionalRoutes } from '@/routes/professionalRoutes';

function ProductErrorFallback() {
  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center px-6 py-20 text-center">
      <div className="w-16 h-16 bg-[#F7F5EE] rounded-2xl flex items-center justify-center mx-auto mb-5">
        <span className="text-2xl">🌿</span>
      </div>
      <h2 className="text-xl font-bold text-[#1A2E12] mb-2">This ritual took a moment to rest.</h2>
      <p className="text-sm text-[#6B7B6E] mb-6 max-w-sm">
        It happens to the best botanicals. Let's get you back on track.
      </p>
      <a
        href="/shop"
        className="px-6 py-2.5 bg-[#BDD630] text-[#1A2E12] rounded-full font-semibold text-sm hover:bg-[#97c23c] transition-colors"
      >
        Back to the Garden
      </a>
    </div>
  );
}

// Layout wrapper that excludes Header/Footer for member pages
function MainLayout({ children }: { children: React.ReactNode }) {
  return (
    <>
      <Header />
      <main>{children}</main>
      <Footer />
      <LeroyChat />
    </>
  );
}

// Member portal layout - no header/footer, handles its own layout
function MemberPortal() {
  return (
    <MemberAuthProvider>
      <MemberRoutes />
    </MemberAuthProvider>
  );
}

// Professional portal layout
function ProfessionalPortal() {
  return (
    <ProfessionalAuthProvider>
      <ProfessionalRoutes />
    </ProfessionalAuthProvider>
  );
}

export function App() {
  return (
    <HelmetProvider>
    <BrowserRouter>
      <SiteConfigProvider>
        <DynamicFonts />
        <ErrorBoundary>
          <div className="min-h-screen bg-[#F7F5EE]">
          <Routes>
            {/* Member Portal Routes - no main layout */}
            <Route path="/member/*" element={
              <ErrorBoundary>
                <MemberPortal />
              </ErrorBoundary>
            } />

            {/* Professional Portal Routes - no main layout */}
            <Route path="/professional/*" element={
              <ErrorBoundary>
                <ProfessionalPortal />
              </ErrorBoundary>
            } />

            {/* Main Site Routes - with header/footer */}
            <Route path="/" element={<MainLayout><ErrorBoundary><Home /></ErrorBoundary></MainLayout>} />
            <Route path="/shop" element={<MainLayout><ErrorBoundary><Shop /></ErrorBoundary></MainLayout>} />
            <Route path="/product/:productId" element={<MainLayout><ErrorBoundary fallback={<ProductErrorFallback />}><ProductDetail /></ErrorBoundary></MainLayout>} />
            <Route path="/skin-analysis" element={<MainLayout><ErrorBoundary><SkinAnalysisWizard /></ErrorBoundary></MainLayout>} />
            <Route path="/profile" element={<MainLayout><ErrorBoundary><Profile /></ErrorBoundary></MainLayout>} />
            <Route path="/regimen" element={<MainLayout><ErrorBoundary><Regimen /></ErrorBoundary></MainLayout>} />
            <Route path="/agent-portal" element={<MainLayout><ErrorBoundary><AgentPortal /></ErrorBoundary></MainLayout>} />
            <Route path="/about" element={<MainLayout><ErrorBoundary><About /></ErrorBoundary></MainLayout>} />
            <Route path="/blog" element={<MainLayout><ErrorBoundary><Blog /></ErrorBoundary></MainLayout>} />
            <Route path="/blog/:slug" element={<MainLayout><ErrorBoundary><Article /></ErrorBoundary></MainLayout>} />
            <Route path="/checkout" element={<MainLayout><ErrorBoundary><CheckoutPage /></ErrorBoundary></MainLayout>} />
            <Route path="/batch-analysis" element={<MainLayout><ErrorBoundary><BatchAnalysisPage /></ErrorBoundary></MainLayout>} />
            <Route path="/design-system" element={<MainLayout><ErrorBoundary><DesignSystemShowcase /></ErrorBoundary></MainLayout>} />
            <Route path="/admin" element={<MainLayout><ErrorBoundary><AdminPortal /></ErrorBoundary></MainLayout>} />
            <Route path="/admin/shop" element={<MainLayout><ErrorBoundary><ShopBackofficePage /></ErrorBoundary></MainLayout>} />

            {/* Static content pages — previously linked but never built */}
            <Route path="/contact" element={<MainLayout><ErrorBoundary><Contact /></ErrorBoundary></MainLayout>} />
            <Route path="/faq" element={<MainLayout><ErrorBoundary><FAQ /></ErrorBoundary></MainLayout>} />
            <Route path="/shipping-returns" element={<MainLayout><ErrorBoundary><ShippingReturns /></ErrorBoundary></MainLayout>} />
            <Route path="/privacy" element={<MainLayout><ErrorBoundary><Privacy /></ErrorBoundary></MainLayout>} />
            <Route path="/terms" element={<MainLayout><ErrorBoundary><Terms /></ErrorBoundary></MainLayout>} />

            {/* Legacy redirects */}
            <Route path="/shipping" element={<Navigate to="/shipping-returns" replace />} />
            <Route path="/returns" element={<Navigate to="/shipping-returns" replace />} />
            <Route path="/cookies" element={<Navigate to="/privacy" replace />} />
            <Route path="/login" element={<Navigate to="/member/login" replace />} />
            <Route path="/signup" element={<Navigate to="/member/create-account" replace />} />
            <Route path="/dashboard" element={<Navigate to="/member/dashboard" replace />} />

            {/* Catch-all — without this an unmatched URL rendered a blank page */}
            <Route path="*" element={<MainLayout><NotFound /></MainLayout>} />
          </Routes>
        </div>
      </ErrorBoundary>
      </SiteConfigProvider>
    </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
